import { useReducer, useEffect, useState } from "react";
import { db } from "../firebase/config";
import {
  collection,
  doc,
  addDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  getDoc,
  writeBatch,
  serverTimestamp,
  query,
  where,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { useCollection } from "./useCollection";

let initialState = {
  document: null,
  isPending: false,
  error: null,
  success: null,
};

const firestoreReducer = (state, action) => {
  switch (action.type) {
    case "IS_PENDING":
      return { document: null, success: null, error: null, isPending: true };
    case "ADDED_DOCUMENT":
      return {
        isPending: false,
        document: action.payload,
        success: true,
        error: null,
      };
    case "ERROR":
      return {
        isPending: false,
        document: null,
        success: false,
        error: action.payload,
      };
    default:
      return state;
  }
};

export const useDocs = (collectionid) => {
  const [response, dispatch] = useReducer(firestoreReducer, initialState);
  const [isCancelled, setIsCancelled] = useState(false);
  const [mydoc, setMydoc] = useState(null);
  const [error, setError] = useState(null);
  const [mydocPending, setMydocPending] = useState(false);
  useCollection("vtec_assets");

  const dispatchIfNotCancelled = (action) => {
    if (!isCancelled) {
      dispatch(action);
    }
  };

  const deleteAsset = async (asset) => {
    try {
      const documentRef = doc(db, "vtec_assets", asset.id);
      await deleteDoc(documentRef);
      console.log("deleted");
    } catch (err) {
      console.log(err.message);
    }
  };

  // Create a new AI Feature
  const addAIFeature = async (newdoc, user) => {
    dispatch({ type: "IS_PENDING" });
    console.log(newdoc, user, "user");
    try {
      const addedDocument = await addDoc(collection(db, "ai_features"), {
        ...newdoc,
        userid: user?.uid || null,
        email: user?.email || null,
        createdAt: serverTimestamp(),
      });

      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      });

      console.log(`[ai_features] added feature: ${addedDocument.id}`);
      return addedDocument;
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.error("Error adding AI feature:", err.message);
      return null;
    }
  };

  const updateAIFeature = async (asset) => {
    dispatch({ type: "IS_PENDING" }); // Assuming you're using Redux
    try {
      const { id, ...rest } = asset;
      // Append timestamp
      const updatedData = {
        ...rest,
        createdAt: serverTimestamp(), // Firestore server time
      };
      const addedDocument = await setDoc(
        doc(db, "ai_features", asset.id),
        updatedData,
        { merge: true }
      );

      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      }); // Assuming you're using Redux
      console.log("Document updated successfully");
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message }); // Assuming you're using Redux
      console.error("Failed to update document:", err);
    }
  };

  const deleteAiFeatures = async (asset) => {
    try {
      const documentRef = doc(db, "ai_features", asset.id);
      await deleteDoc(documentRef);
      console.log("deleted");
    } catch (err) {
      console.log(err.message);
    }
  };

  // generic delete function that checks both collections
  const deleteAssetFromAny = async (asset) => {
    try {
      // check in vtec_assets
      const vtecRef = doc(db, "vtec_assets", asset.id);
      const vtecSnap = await getDoc(vtecRef);

      if (vtecSnap.exists()) {
        await deleteDoc(vtecRef);
        console.log(`Deleted from vtec_assets: ${asset.id}`);
        return;
      }

      // check in ai_features
      const aiRef = doc(db, "ai_features", asset.id);
      const aiSnap = await getDoc(aiRef);

      if (aiSnap.exists()) {
        await deleteDoc(aiRef);
        console.log(`Deleted from ai_features: ${asset.id}`);
        return;
      }

      // if not found in either
      console.log(
        `Asset ${asset.id} does not exist in vtec_assets or ai_features`
      );
    } catch (err) {
      console.error("Error deleting asset:", err.message);
    }
  };

  // generic update function that checks both collections
  const updateAssetFromAny = async (asset) => {
    try {
      // check in vtec_assets
      const vtecRef = doc(db, "vtec_assets", asset.id);
      const vtecSnap = await getDoc(vtecRef);

      if (vtecSnap.exists()) {
        await updateAsset(asset);
        console.log(`Deleted from vtec_assets: ${asset.id}`);
        return;
      }

      // check in ai_features
      const aiRef = doc(db, "ai_features", asset.id);
      const aiSnap = await getDoc(aiRef);

      if (aiSnap.exists()) {
        await updateAIFeature(asset);
        console.log(`Deleted from ai_features: ${asset.id}`);
        return;
      }

      // if not found in either
      console.log(
        `Asset ${asset.id} does not exist in vtec_assets or ai_features`
      );
    } catch (err) {
      console.error("Error updating asset:", err.message);
    }
  };

  // Get all AI Features
  const getAllAIFeatures = async () => {
    try {
      const collRef = collection(db, "ai_features");
      const snapshot = await getDocs(collRef);
      const features = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return features;
    } catch (err) {
      console.error("Error fetching ai_features:", err.message);
      return [];
    }
  };

  const addDocument = async (newdoc, user) => {
    dispatch({ type: "IS_PENDING" });
    try {
      const addedDocument = await addDoc(collection(db, "vtec_assets"), {
        ...newdoc,
        userid: user.uid,
        email: user.email,
        createdAt: serverTimestamp(),
      });

      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      });
      console.log(addedDocument);
      return addedDocument;
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.log(err.message);
      return null;
    }
  };

  const addSeed = async (newdoc, user, meta) => {
    dispatch({ type: "IS_PENDING" });
    try {
      const addedDocument = await addDoc(collection(db, "seeds"), {
        seedData: newdoc,
        userid: user.uid,
        email: user.email,
        title: meta.title,
        desc: meta.desc,
        imgurl: meta.imgurl,
        createdAt: serverTimestamp(),
      });

      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      });
      console.log(addedDocument);
      return addedDocument;
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.log(err.message);
      return null;
    }
  };

  const updatePosition = async (asset) => {
    dispatch({ type: "IS_PENDING" }); // Assuming you're using Redux
    try {
      const addedDocument = await setDoc(
        doc(db, "vtec_assets", asset.id),
        { Top: asset.Top, Left: asset.Left },
        { merge: true }
      );
      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      }); // Assuming you're using Redux
      console.log("Document updated successfully");
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message }); // Assuming you're using Redux
      console.error("Failed to update document:", err);
    }
  };

  const updateAsset = async (asset) => {
    dispatch({ type: "IS_PENDING" });
    try {
      const { id, ...rest } = asset;

      const cleanedData = Object.fromEntries(
        Object.entries(rest).filter(([, v]) => v !== undefined)
      );

      const updatedData = {
        ...cleanedData,
        createdAt: serverTimestamp(),
      };
      const addedDocument = await setDoc(
        doc(db, "vtec_assets", asset.id),
        updatedData,
        { merge: true }
      );
      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      });
      console.log("Document updated successfully");
      return true;
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.error("Failed to update document:", err);
      throw err;
    }
  };

  const setDocument = async (vtecAsset, data) => {
    dispatch({ type: "IS_PENDING" });
    console.log(vtecAsset.id);
    try {
      const addedDocument = await setDoc(
        doc(db, "vtec_assets", vtecAsset.id),
        data,
        { merge: true }
      );

      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      });
      console.log("done");
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.log(`failed to write ${err}`);
    }
  };

  const getUserDeets = async (userId) => {
    try {
      let ref = doc(db, "partnerDetails", userId);
      const docSnap = await getDoc(ref);
      console.log(docSnap);
      return docSnap.data();
    } catch (err) {
      return { approved: false, partnerAccount: null, privateId: null };
    }
  };

  const setUserDeets = async (userId, email, emailVerified, isNewUser) => {
    dispatch({ type: "IS_PENDING" });
    console.log("setting the user");
    console.log(userId);
    let isApproved = false;
    try {
      const domain = extractDomain(email);
      const privateAccount = domain ? await getPrivateAccount(domain) : null;
      if (privateAccount) {
        if (privateAccount.hasOwnProperty("isApproved")) {
          isApproved = privateAccount.isApproved;
        }
      } else {
        await addPrivateAccount(domain, {
          name: "",
          svg: "",
          isApproved: false,
        });
      }

      const addedDocument = await setDoc(
        doc(db, "partnerDetails", userId),
        {
          approved: isApproved,
          partnerAccount: null,
          email: email,
          privateId: null,
          isNewUser: isNewUser,
          emailVerified: emailVerified,
        },
        { merge: true }
      );
      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      });
      console.log("done");
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.log(`failed to write ${err}`);
    }
  };

  const isExistDomain = async (email) => {
    const domain = extractDomain(email);
    const privateAccount = domain ? await getPrivateAccount(domain) : null;
    console.log(privateAccount, "privateAxxout");
    return privateAccount.exist;
  };

  const extractDomain = (email) => {
    const domain = email.split("@")[1];
    return domain || null;
  };

  const deletePartner = async (partner) => {
    try {
      const documentRef = doc(db, "partnerDetails", partner.id);
      await deleteDoc(documentRef);
      console.log("deleted");
    } catch (err) {
      console.log(err.message);
    }
  };

  const deleteSeed = async (seed) => {
    try {
      const documentRef = doc(db, "seeds", seed.id);
      await deleteDoc(documentRef);
      console.log("deleted");
    } catch (err) {
      console.log(err.message);
    }
  };

  const ReadDocument = (collectionId, docId) => {
    //fires once and whenever the collection changes
    useEffect(() => {
      setMydocPending(true);
      //set up a real time listener to a firestore collection
      let ref = doc(db, collectionid, docId);
      const unsubscribe = onSnapshot(
        ref,
        (snapshot) => {
          if (snapshot.exists()) {
            console.log("exists");
            setMydoc(snapshot.data());
          } else {
            console.log("doc doesnt exist");
            // setOpportunity(docId, null, []);
          }
          setMydocPending(false);
          setError(null);
        },
        (error) => {
          console.log(error);
          setMydocPending(false);
          setError("could not fetch the doc");
        }
      );
      //unsub on unmount
      return () => unsubscribe();
    }, [collectionId, docId]);
    return { mydoc, error, mydocPending };
  };
  //for updating partner accounts and parterns
  const updateDocument = async (id, obj, collectionType) => {
    dispatch({ type: "IS_PENDING" });
    try {
      const addedDocument = await setDoc(doc(db, collectionType, id), obj, {
        merge: true,
      });
      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      });
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.error(err); // Log the entire error object for better debugging
    }
  };
  const updateMultipleDocuments = async (updates) => {
    const batch = writeBatch(db); // Initialize the batch
    // Iterate over the updates array and add each update to the batch
    updates.forEach(({ collection, id, data }) => {
      const docRef = doc(db, collection, id);
      batch.update(docRef, data);
    });
    dispatch({ type: "IS_PENDING" });
    try {
      await batch.commit(); // Commit the batch
      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: updates, // You can customize the payload as needed
      });
      console.log("Documents updated successfully");
    } catch (error) {
      dispatchIfNotCancelled({
        type: "ERROR",
        payload: error.message,
      });
      console.error("Error updating documents: ", error);
    }
  };

  const setEmailVerified = async (userId, emailVerified) => {
    dispatch({ type: "IS_PENDING" });
    try {
      const addedDocument = await setDoc(
        doc(db, "partnerDetails", userId),
        {
          emailVerified: emailVerified,
        },
        { merge: true }
      );
      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      });
      console.log("done");
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.log(`failed to write ${err}`);
    }
  };
  const addViewDocument = async (newdoc) => {
    const { id, ...rest } = newdoc;
    dispatch({ type: "IS_PENDING" });
    try {
      const collectionRef = collection(db, "views");
      if (newdoc.isAdminDefault) {
        const q = query(collectionRef, where("isAdminDefault", "==", true));
        const querySnapshot = await getDocs(q);

        const batch = writeBatch(db);
        querySnapshot.forEach((doc) => {
          const docRef = doc.ref;
          if (doc.id !== id) {
            // Exclude the document we're currently saving or updating
            batch.update(docRef, { isAdminDefault: false });
          }
        });
        await batch.commit();
      }
      const docRef = id ? doc(db, "views", id) : collectionRef;

      const addedDocument = id
        ? await setDoc(docRef, { ...rest, createdAt: serverTimestamp() })
        : await addDoc(docRef, { ...rest, createdAt: serverTimestamp() });

      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      });
      console.log(addedDocument);
      return addedDocument;
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.log(err.message);
      return null;
    }
  };
  const deleteDocument = async (id, collection) => {
    try {
      const documentRef = doc(db, collection, id);
      await deleteDoc(documentRef);
      console.log("deleted");
    } catch (err) {
      console.log(err.message);
    }
  };
  const addViewsToPartner = async (data, userId) => {
    dispatch({ type: "IS_PENDING" });
    try {
      const addedDocument = await setDoc(
        doc(db, "partnerDetails", userId),
        data,
        {
          merge: true,
        }
      );
      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: addedDocument,
      });
      console.log("done");
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.log(`failed to write ${err}`);
    }
  };

  useEffect(() => {
    return () => setIsCancelled(true);
  }, [mydoc]);

  const addPrivateAccount = async (id, newdoc) => {
    dispatch({ type: "IS_PENDING" });
    try {
      let documentRef;
      if (id) {
        documentRef = doc(db, "privateAccounts", id);
        await setDoc(documentRef, {
          ...newdoc,
          createdAt: serverTimestamp(),
        });
      } else {
        documentRef = await addDoc(collection(db, "privateAccounts"), {
          ...newdoc,
          createdAt: serverTimestamp(),
        });
      }

      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: { id: documentRef.id, ...newdoc },
      });
      return documentRef;
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.error("Error adding document:", err.message);
      return null;
    }
  };

  const getPrivateAccount = async (emailSuffix) => {
    try {
      let ref = doc(db, "privateAccounts", emailSuffix);
      const docSnap = await getDoc(ref);
      if (docSnap.exists()) {
        console.log(docSnap);
        return {
          exist: true,
          ...docSnap.data(),
        };
      }
      const collRef = collection(db, "privateAccounts");
      const linkedAccountsQuery = query(
        collRef,
        where("childEmails", "array-contains", emailSuffix)
      );
      const linkedAccountsQuerySnapshot = await getDocs(linkedAccountsQuery);

      if (!linkedAccountsQuerySnapshot.empty) {
        return {
          exist: true,
          ...linkedAccountsQuerySnapshot.docs[0].data(),
        };
      }

      // not found
      return {
        exist: false,
      };
    } catch (err) {
      console.log(err, emailSuffix);
      return {
        exist: false,
        svg: null,
      };
    }
  };
  const updateUnapprovedUsers = async (partnerAccount, isApproved) => {
    try {
      const usersRef = collection(db, "partnerDetails");

      // Firestore query to fetch only unapproved users
      const q = query(
        usersRef,
        where("approved", "==", isApproved ? false : true)
      );

      const querySnapshot = await getDocs(q);
      const batchUpdates = [];

      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.email.endsWith(partnerAccount)) {
          batchUpdates.push(updateDoc(doc.ref, { approved: isApproved }));
        }
      });

      await Promise.all(batchUpdates);
      console.log("Updated users successfully.");
    } catch (error) {
      console.error("Error updating users:", error);
    }
  };
  const getAdminRecord = async (user) => {
    try {
      let ref = doc(db, "user_admin", user && user.uid ? user.uid : "empty");
      const docSnap = await getDoc(ref);
      if (docSnap.exists()) {
        const result = docSnap.data();
        return result.admin;
      } else {
        if (user && user.uid) {
          await setDoc(
            doc(db, "user_admin", user.uid),
            { admin: false },
            { merge: true }
          );
        }
        return false;
      }
    } catch (error) {
      console.log(`failed to get admin record ${error}`);
      return false;
    }
  };

  const addInstance = async (id, newdoc) => {
    dispatch({ type: "IS_PENDING" });
    try {
      let documentRef;
      if (id) {
        documentRef = doc(db, "demoInstances", id);
        await setDoc(documentRef, {
          ...newdoc,
          createdAt: serverTimestamp(),
        });
      } else {
        documentRef = await addDoc(collection(db, "demoInstances"), {
          ...newdoc,
          createdAt: serverTimestamp(),
        });
      }

      dispatchIfNotCancelled({
        type: "ADDED_DOCUMENT",
        payload: { id: documentRef.id, ...newdoc },
      });
      return documentRef;
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
      console.error("Error adding document:", err.message);
      return null;
    }
  };

  const getAllInstances = async () => {
    try {
      const collRef = collection(db, "demoInstances");
      const snapshot = await getDocs(collRef);
      const instances = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return instances;
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  const addExperience = async (newExp, user) => {
    try {
      const addedExperience = await addDoc(collection(db, "experiences"), {
        ...newExp,
        userid: user?.uid || null,
        createdAt: serverTimestamp(),
      });

      console.log(`[experiences] added experience: ${addedExperience.id}`);
      return addedExperience;
    } catch (err) {
      console.error("Error adding experience:", err.message);
      return null;
    }
  };

  /**
   * Add a bubble and its libraries together
   * @param {string} experienceId - parent experience ID
   * @param {Object} newBubble - bubble data with content inside
   * @param {Object} user - logged-in user info
   */
  const addBubble = async (experienceId, newBubble, user) => {
    try {
      const { content = {}, ...bubbleData } = newBubble;
      const { library = [], ...contentData } = content;

      // Step 1: Add bubble without libraries (just store IDs for now)
      const bubbleRef = await addDoc(collection(db, "bubbles"), {
        ...bubbleData,
        content: {
          ...contentData,
          library: [], // placeholder, we'll push IDs later
        },
        experienceId,
        userid: user?.uid || null,
        createdAt: serverTimestamp(),
      });

      console.log(
        `[bubbles] added bubble: ${bubbleRef.id} under experience ${experienceId}`
      );

      // Step 2: Add libraries and collect their IDs
      const libraryIds = [];
      for (const lib of library) {
        let libId = lib.id;

        if (!libId) {
          // if no id provided, Firestore generates one
          const libRef = await addDoc(collection(db, "libraries"), {
            ...lib,
            bubbleId: bubbleRef.id, // optional, only if you want reverse mapping
            userid: user?.uid || null,
            createdAt: serverTimestamp(),
          });
          libId = libRef.id;
          console.log(
            `[libraries] added new library ${libId} under bubble ${bubbleRef.id}`
          );
        } else {
          // If library already exists in global collection, just reference it
          const libRef = doc(db, "libraries", libId);
          await setDoc(
            libRef,
            {
              ...lib,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          console.log(
            `[libraries] linked existing library ${libId} to bubble ${bubbleRef.id}`
          );
        }

        libraryIds.push(libId);
      }

      // Step 3: Update bubble document with library IDs
      await updateDoc(bubbleRef, {
        "content.library": libraryIds,
      });

      console.log(`[bubbles] updated bubble ${bubbleRef.id} with library IDs`);

      return bubbleRef;
    } catch (err) {
      console.error("Error adding bubble with libraries:", err.message);
      return null;
    }
  };

  /**
   * Update a bubble and its linked libraries under a target experience
   * @param {Object[]} bubbles - bubbles array (if needed for context)
   * @param {string} targetBubbleId - Firestore doc ID of the bubble
   * @param {string} targetExperienceId - Firestore doc ID of the experience
   * @param {Object} bubbleFields - fields to update inside the bubble
   * @param {Object} contentFields - content object (including library IDs)
   * @param {Object[]} libraries - array of library objects to update
   */
  const updateExperienceWithBubbles = async (
    targetBubbleId,
    targetExperienceId,
    bubbleFields = {},
    contentFields = {},
    libraries = [],
    user = null
  ) => {
    try {
      // 1. Query the bubble by its fields
      const bubblesQuery = query(
        collection(db, "bubbles"),
        where("experienceId", "==", targetExperienceId),
        where("id", "==", targetBubbleId)
      );
      const bubblesSnap = await getDocs(bubblesQuery);

      if (bubblesSnap.empty) {
        throw new Error(
          `Bubble ${targetBubbleId} not found under experience ${targetExperienceId}`
        );
      }

      // 2. Use the actual Firestore doc ID, not the "id" field
      const bubbleDoc = bubblesSnap.docs[0];
      const bubbleRef = bubbleDoc.ref;

      await setDoc(
        bubbleRef,
        {
          ...bubbleFields,
          content: contentFields,
          experienceId: targetExperienceId,
          userid: user?.uid || null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      console.log(
        `[bubbles] updated ${targetBubbleId} under experience ${targetExperienceId}`
      );

      const libraryIds = [];
      for (const lib of libraries) {
        if (!lib.id) continue; // skip if no ID
        let libId = lib.id;

        // Query for existing library by ID
        const libQuery = query(
          collection(db, "libraries"),
          where("id", "==", libId)
        );
        const libSnap = await getDocs(libQuery);

        if (!libSnap.empty) {
          // Existing library → update
          const libRef = libSnap.docs[0].ref;
          await setDoc(
            libRef,
            {
              ...lib,
              userid: user?.uid || null,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          console.log(
            `[libraries] updated ${lib.id} under bubble ${targetBubbleId}`
          );
        } else {
          // New library → create
          const newLibRef = doc(collection(db, "libraries"));
          await setDoc(newLibRef, {
            ...lib,
            id: lib.id,
            userid: user?.uid || null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          console.log(
            `[libraries] created ${lib.id} under bubble ${targetBubbleId}`
          );
        }

        libraryIds.push(libId);
      }

      // Step 3: Update bubble document with library IDs
      await updateDoc(bubbleRef, {
        "content.library": libraryIds,
      });

      return { bubbleId: targetBubbleId, experienceId: targetExperienceId };
    } catch (err) {
      console.error("Error updating bubble:", err.message);
      return false;
    }
  };

  /**
   * Fetch full experience with bubbles and libraries expanded
   * @param {string} experienceId - Firestore doc ID of experience
   */
  const getExperienceWithBubbles = async (experienceId) => {
    try {
      // 1. Get experience doc
      const expQuery = query(
        collection(db, "experiences"),
        where("id", "==", experienceId)
      );
      const expSnap = await getDocs(expQuery);

      if (expSnap.empty) {
        throw new Error("Experience not found");
      }

      const experienceData = expSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }))[0];

      // 2. Get all bubbles linked to this experience
      const bubblesQuery = query(
        collection(db, "bubbles"),
        where("experienceId", "==", experienceId)
      );
      const bubblesSnap = await getDocs(bubblesQuery);

      const bubbles = [];
      for (const bubbleDoc of bubblesSnap.docs) {
        const bubbleData = { id: bubbleDoc.id, ...bubbleDoc.data() };
        const { content = {} } = bubbleData;

        // 3. If bubble has library IDs, fetch full objects
        let libraries = [];
        if (Array.isArray(content.library) && content.library.length > 0) {
          const libQuery = query(
            collection(db, "libraries"),
            where("id", "in", content.library) // match by id
          );
          const libSnap = await getDocs(libQuery);
          libraries = libSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        }

        bubbles.push({
          ...bubbleData,
          content: {
            ...content,
            library: libraries,
          },
        });
      }

      // 4. Return combined structure
      return [{ ...experienceData, bubbles }];
    } catch (err) {
      console.error("Error fetching experience with bubbles:", err.message);
      return null;
    }
  };

  /**
 * Fetch all library documents from the "libraries" collection.
 * @returns {Promise<Array>} List of library objects with their Firestore IDs.
 */
  const getAllLibraries = async () => {
  try {
    // Reference to the "libraries" collection
    const librariesRef = collection(db, "libraries");

    // Fetch all documents
    const snapshot = await getDocs(librariesRef);

    // Transform into structured array
    const libraries = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log(" Libraries fetched successfully:", libraries.length);
    return libraries;
  } catch (error) {
    console.error(" Error fetching libraries:", error);
    return [];
  }
};

  // const getAllBubbles = async () => {
  //   try {
  //     // Fetch all bubbles from the collection
  //     const bubblesCollection = collection(db, "bubbles");
  //     const querySnapshot = await getDocs(bubblesCollection);

  //     // Map each document into an object
  //     const bubbles = querySnapshot.docs.map((doc) => ({
  //       id: doc.id,
  //       ...doc.data(),
  //     }));

  //     const newBubbles = [];

  //     for (const items of bubbles) {
  //       let libraries = [];

  //       if (
  //         Array.isArray(items.content.library) &&
  //         items.content.library.length > 0
  //       ) {
  //         // Firestore 'in' query supports max 10 elements
  //         const libQuery = query(
  //           collection(db, "libraries"),
  //           where("id", "in", items.content.library.slice(0, 10))
  //         );

  //         const libSnap = await getDocs(libQuery);
  //         libraries = libSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  //       }

  //       newBubbles.push({
  //         ...items,
  //         content: {
  //           ...items.content,
  //           library: libraries,
  //         },
  //       });
  //     }

  //     console.log(newBubbles, "Bubbles with full libraries");
  //     return newBubbles;
  //   } catch (err) {
  //     console.error("Error fetching all bubbles:", err);
  //     return [];
  //   }
  // };

  // const getAllExperiencesWithBubbles = async () => {
  //   try {
  //     const expCollection = collection(db, "experiences");
  //     const expSnap = await getDocs(expCollection);

  //     const experiences = await Promise.all(
  //       expSnap.docs.map(async (expDoc) => {
  //         const expData = { id: expDoc.id, ...expDoc.data() };

  //         // Fetch bubbles for this experience
  //         const bubblesQuery = query(
  //           collection(db, "bubbles"),
  //           where("experienceId", "==", expData.id)
  //         );
  //         const bubblesSnap = await getDocs(bubblesQuery);

  //         const bubbles = await Promise.all(
  //           bubblesSnap.docs.map(async (bubbleDoc) => {
  //             const bubbleData = { id: bubbleDoc.id, ...bubbleDoc.data() };
  //             const { content = {} } = bubbleData;
  //             // Expand libraries
  //             let libraries = [];
  //             if (
  //               Array.isArray(content.library) &&
  //               content.library.length > 0
  //             ) {
  //               const libQuery = query(
  //                 collection(db, "libraries"),
  //                 where("id", "in", content.library.slice(0, 10)) // Firestore limit
  //               );
  //               const libSnap = await getDocs(libQuery);
  //               libraries = libSnap.docs.map((d) => ({
  //                 id: d.id,
  //                 ...d.data(),
  //               }));
  //             }

  //             return {
  //               ...bubbleData,
  //               content: {
  //                 ...content,
  //                 library: libraries,
  //               },
  //             };
  //           })
  //         );

  //         return {
  //           ...expData,
  //           bubbles,
  //         };
  //       })
  //     );

  //     return experiences;
  //   } catch (err) {
  //     console.error("Error fetching experiences:", err);
  //     return [];
  //   }
  // };

  // Delete a specific bubble and its libraries

  const getAllBubbles = async () => {
    try {
      // Fire all reads in parallel
      const [bubblesSnap, librariesSnap] = await Promise.all([
        getDocs(collection(db, "bubbles")),
        getDocs(collection(db, "libraries")),
      ]);

      // Convert snapshots to JS objects
      const bubbles = bubblesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const libraries = librariesSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      // Build a hash map for O(1) library lookup
      const libraryMap = new Map(libraries.map((lib) => [lib.id, lib]));

      // Expand library refs inline
      const enrichedBubbles = bubbles.map((b) => {
        const content = b.content || {};
        const libraryRefs = Array.isArray(content.library)
          ? content.library
          : [];

        const expandedLibraries = libraryRefs
          .map((libId) => libraryMap.get(libId))
          .filter(Boolean); // remove missing refs

        return {
          ...b,
          content: {
            ...content,
            library: expandedLibraries,
          },
        };
      });

      console.log(enrichedBubbles, "Optimized bubbles with full libraries");
      return enrichedBubbles;
    } catch (err) {
      console.error("Error fetching all bubbles:", err);
      return [];
    }
  };

  const getAllExperiencesWithBubbles = async () => {
    try {
      // Fire all collection reads in parallel
      const [expSnap, bubblesSnap, librariesSnap] = await Promise.all([
        getDocs(collection(db, "experiences")),
        getDocs(collection(db, "bubbles")),
        getDocs(collection(db, "libraries")),
      ]);

      // Convert snapshots to arrays
      const experiences = expSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const bubbles = bubblesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const libraries = librariesSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      // Build maps for efficient lookup
      const librariesMap = new Map(libraries.map((lib) => [lib.id, lib]));
      const bubblesByExperience = new Map();

      // Group bubbles by experienceId
      for (const b of bubbles) {
        const list = bubblesByExperience.get(b.experienceId) || [];
        // Expand library references inline
        const expandedLibs =
          Array.isArray(b.content?.library) && b.content.library.length > 0
            ? b.content.library
                .map((lid) => librariesMap.get(lid))
                .filter(Boolean)
            : [];

        list.push({
          ...b,
          content: { ...b.content, library: expandedLibs },
        });
        bubblesByExperience.set(b.experienceId, list);
      }

      // Merge bubbles into experiences
      const enrichedExperiences = experiences.map((exp) => ({
        ...exp,
        bubbles: bubblesByExperience.get(exp.id) || [],
      }));

      return enrichedExperiences;
    } catch (err) {
      console.error("Error fetching experiences:", err);
      return [];
    }
  };

  const deleteBubble = async (bubbleId, experienceId) => {
    try {
      if (!bubbleId || !experienceId) {
        console.log("Invalid bubbleId or experienceId");
        return false;
      }

      // 1. Query the bubble by its fields
      const bubblesQuery = query(
        collection(db, "bubbles"),
        where("id", "==", bubbleId),
        where("experienceId", "==", experienceId)
      );
      const bubbleSnap = await getDocs(bubblesQuery);

      if (bubbleSnap.empty) {
        console.log(
          `No bubble found with id ${bubbleId} under experience ${experienceId}`
        );
        return false;
      }

      // 2. Get the doc reference and data
      const bubbleDoc = bubbleSnap.docs[0];
      const bubbleRef = bubbleDoc.ref;
      const bubbleData = bubbleDoc.data();

      // 3. Delete associated libraries
      const library = bubbleData.content?.library || [];
      for (const lib of library) {
        if (!lib.id) continue;
        const libQuery = query(
          collection(db, "libraries"),
          where("id", "==", lib.id)
        );
        const libSnap = await getDocs(libQuery);
        if (!libSnap.empty) {
          const libRef = libSnap.docs[0].ref;
          await deleteDoc(libRef);
          console.log(`[libraries] deleted ${lib.id} under bubble ${bubbleId}`);
        }
      }

      // 4. Delete the bubble document itself
      await deleteDoc(bubbleRef);
      console.log(
        `[bubbles] deleted ${bubbleId} under experience ${experienceId}`
      );

      return true;
    } catch (err) {
      console.error("Error deleting bubble:", err);
      return false;
    }
  };

  return {
    addDocument,
    deleteDocument,
    deletePartner,
    setDocument,
    updateAsset,
    deleteAsset,
    response,
    ReadDocument,
    getUserDeets,
    setUserDeets,
    mydoc,
    deleteSeed,
    addSeed,
    updatePosition,
    updateDocument,
    updateMultipleDocuments,
    setEmailVerified,
    addViewDocument,
    addViewsToPartner,
    addPrivateAccount,
    getPrivateAccount,
    updateUnapprovedUsers,
    getAdminRecord,
    addInstance,
    getAllInstances,
    extractDomain,
    // new AI Features specific methods
    addAIFeature,
    deleteAssetFromAny,
    deleteAiFeatures,
    updateAssetFromAny,
    updateAIFeature,
    getAllAIFeatures,
    isExistDomain,
    addExperience,
    addBubble,
    updateExperienceWithBubbles,
    getExperienceWithBubbles,
    deleteBubble,
    getAllBubbles,
    getAllExperiencesWithBubbles,
    getAllLibraries
  };
};
